import { FeatureCollection, Feature, Geometry } from 'geojson';

// Site data
export interface Site {
  id: string;
  name: string;
  capacity: number; // MW
  location: {
    lat: number;
    lng: number;
  };
  status: 'operational' | 'maintenance' | 'offline';
}

export const sites: Site[] = [
  {
    id: 'site-1',
    name: 'Desert Vista Solar Farm',
    capacity: 150,
    location: { lat: 33.4484, lng: -112.0740 },
    status: 'operational'
  },
  {
    id: 'site-2',
    name: 'Sunrise Valley PV Plant',
    capacity: 75,
    location: { lat: 34.0522, lng: -118.2437 },
    status: 'operational'
  },
  {
    id: 'site-3',
    name: 'Mountain Ridge Solar',
    capacity: 200,
    location: { lat: 36.1699, lng: -115.1398 },
    status: 'maintenance'
  }
];

// GeoJSON data for solar farm layout
export const solarFarmGeoJSON: FeatureCollection = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {
        id: 'array-1',
        name: 'Array Block 1',
        capacity: 25,
        status: 'operational',
        inverters: 4,
        modules: 8000
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [-112.0760, 33.4500],
          [-112.0740, 33.4500],
          [-112.0740, 33.4480],
          [-112.0760, 33.4480],
          [-112.0760, 33.4500]
        ]]
      }
    },
    {
      type: 'Feature',
      properties: {
        id: 'array-2',
        name: 'Array Block 2',
        capacity: 25,
        status: 'operational',
        inverters: 4,
        modules: 8000
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [-112.0735, 33.4500],
          [-112.0715, 33.4500],
          [-112.0715, 33.4480],
          [-112.0735, 33.4480],
          [-112.0735, 33.4500]
        ]]
      }
    },
    {
      type: 'Feature',
      properties: {
        id: 'array-3',
        name: 'Array Block 3',
        capacity: 25,
        status: 'maintenance',
        inverters: 4,
        modules: 8000
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [-112.0760, 33.4475],
          [-112.0740, 33.4475],
          [-112.0740, 33.4455],
          [-112.0760, 33.4455],
          [-112.0760, 33.4475]
        ]]
      }
    },
    {
      type: 'Feature',
      properties: {
        id: 'substation',
        name: 'Main Substation',
        type: 'substation',
        voltage: '138kV'
      },
      geometry: {
        type: 'Point',
        coordinates: [-112.0720, 33.4510]
      }
    }
  ]
};

// SLD (Single Line Diagram) data
export interface SLDNode {
  id: string;
  type: 'substation' | 'transformer' | 'inverter' | 'combiner' | 'string';
  label: string;
  status: 'operational' | 'maintenance' | 'fault' | 'offline';
  data?: any;
}

export interface SLDEdge {
  id: string;
  source: string;
  target: string;
  type: 'ac' | 'dc' | 'ground';
  label?: string;
}

export const sldNodes: SLDNode[] = [
  { id: 'sub-1', type: 'substation', label: 'Main Substation 138kV', status: 'operational' },
  { id: 'tx-1', type: 'transformer', label: 'TX-1 138/34.5kV', status: 'operational' },
  { id: 'inv-1', type: 'inverter', label: 'INV-1 (2.5MW)', status: 'operational' },
  { id: 'inv-2', type: 'inverter', label: 'INV-2 (2.5MW)', status: 'operational' },
  { id: 'inv-3', type: 'inverter', label: 'INV-3 (2.5MW)', status: 'maintenance' },
  { id: 'inv-4', type: 'inverter', label: 'INV-4 (2.5MW)', status: 'operational' },
  { id: 'cmb-1-1', type: 'combiner', label: 'CMB-1-1', status: 'operational' },
  { id: 'cmb-1-2', type: 'combiner', label: 'CMB-1-2', status: 'operational' },
  { id: 'cmb-2-1', type: 'combiner', label: 'CMB-2-1', status: 'operational' },
  { id: 'cmb-2-2', type: 'combiner', label: 'CMB-2-2', status: 'operational' },
];

export const sldEdges: SLDEdge[] = [
  { id: 'e1', source: 'sub-1', target: 'tx-1', type: 'ac', label: '138kV' },
  { id: 'e2', source: 'tx-1', target: 'inv-1', type: 'ac', label: '34.5kV' },
  { id: 'e3', source: 'tx-1', target: 'inv-2', type: 'ac', label: '34.5kV' },
  { id: 'e4', source: 'tx-1', target: 'inv-3', type: 'ac', label: '34.5kV' },
  { id: 'e5', source: 'tx-1', target: 'inv-4', type: 'ac', label: '34.5kV' },
  { id: 'e6', source: 'cmb-1-1', target: 'inv-1', type: 'dc', label: '1500V DC' },
  { id: 'e7', source: 'cmb-1-2', target: 'inv-1', type: 'dc', label: '1500V DC' },
  { id: 'e8', source: 'cmb-2-1', target: 'inv-2', type: 'dc', label: '1500V DC' },
  { id: 'e9', source: 'cmb-2-2', target: 'inv-2', type: 'dc', label: '1500V DC' },
];

// Event History data
export interface Event {
  id: string;
  timestamp: Date;
  source: 'SCADA' | 'Manual' | 'Alarm' | 'Maintenance' | 'Weather';
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  description: string;
  affectedComponents?: string[];
}

export const events: Event[] = [
  {
    id: 'evt-1',
    timestamp: new Date('2024-01-15T08:00:00'),
    source: 'SCADA',
    type: 'info',
    title: 'Daily Production Start',
    description: 'Solar production started at sunrise. All systems operational.',
    affectedComponents: ['array-1', 'array-2', 'array-3']
  },
  {
    id: 'evt-2',
    timestamp: new Date('2024-01-15T10:30:00'),
    source: 'Alarm',
    type: 'warning',
    title: 'Inverter Temperature Warning',
    description: 'INV-3 temperature exceeded threshold (85°C)',
    affectedComponents: ['inv-3']
  },
  {
    id: 'evt-3',
    timestamp: new Date('2024-01-15T11:00:00'),
    source: 'Maintenance',
    type: 'warning',
    title: 'Scheduled Maintenance Started',
    description: 'Preventive maintenance on Array Block 3 and INV-3',
    affectedComponents: ['array-3', 'inv-3']
  },
  {
    id: 'evt-4',
    timestamp: new Date('2024-01-15T14:00:00'),
    source: 'Weather',
    type: 'info',
    title: 'Cloud Coverage Detected',
    description: 'Partial cloud coverage reducing production by 15%',
    affectedComponents: []
  },
  {
    id: 'evt-5',
    timestamp: new Date('2024-01-15T15:30:00'),
    source: 'Maintenance',
    type: 'success',
    title: 'Maintenance Completed',
    description: 'INV-3 maintenance completed successfully',
    affectedComponents: ['inv-3']
  },
  {
    id: 'evt-6',
    timestamp: new Date('2024-01-15T18:00:00'),
    source: 'SCADA',
    type: 'info',
    title: 'Daily Production End',
    description: 'Solar production ended at sunset. Total generation: 1,245 MWh',
    affectedComponents: []
  }
];

// Documents data
export interface Document {
  id: string;
  name: string;
  type: 'pdf' | 'excel' | 'word' | 'image' | 'folder';
  path: string;
  size?: number;
  modifiedDate: Date;
  children?: Document[];
}

export const documents: Document[] = [
  {
    id: 'doc-1',
    name: 'Operations',
    type: 'folder',
    path: '/Operations',
    modifiedDate: new Date('2024-01-10'),
    children: [
      {
        id: 'doc-1-1',
        name: 'Daily Reports',
        type: 'folder',
        path: '/Operations/Daily Reports',
        modifiedDate: new Date('2024-01-15'),
        children: [
          {
            id: 'doc-1-1-1',
            name: 'Daily_Report_2024-01-15.pdf',
            type: 'pdf',
            path: '/Operations/Daily Reports/Daily_Report_2024-01-15.pdf',
            size: 2456000,
            modifiedDate: new Date('2024-01-15T20:00:00')
          },
          {
            id: 'doc-1-1-2',
            name: 'Production_Summary_2024-01-15.xlsx',
            type: 'excel',
            path: '/Operations/Daily Reports/Production_Summary_2024-01-15.xlsx',
            size: 854000,
            modifiedDate: new Date('2024-01-15T19:30:00')
          }
        ]
      },
      {
        id: 'doc-1-2',
        name: 'O&M Manual.pdf',
        type: 'pdf',
        path: '/Operations/O&M Manual.pdf',
        size: 15420000,
        modifiedDate: new Date('2023-12-01')
      }
    ]
  },
  {
    id: 'doc-2',
    name: 'Technical',
    type: 'folder',
    path: '/Technical',
    modifiedDate: new Date('2024-01-05'),
    children: [
      {
        id: 'doc-2-1',
        name: 'Single Line Diagram.pdf',
        type: 'pdf',
        path: '/Technical/Single Line Diagram.pdf',
        size: 3200000,
        modifiedDate: new Date('2023-11-15')
      },
      {
        id: 'doc-2-2',
        name: 'Inverter Specifications.pdf',
        type: 'pdf',
        path: '/Technical/Inverter Specifications.pdf',
        size: 1800000,
        modifiedDate: new Date('2023-10-20')
      },
      {
        id: 'doc-2-3',
        name: 'Module Datasheet.pdf',
        type: 'pdf',
        path: '/Technical/Module Datasheet.pdf',
        size: 950000,
        modifiedDate: new Date('2023-10-20')
      }
    ]
  },
  {
    id: 'doc-3',
    name: 'Maintenance',
    type: 'folder',
    path: '/Maintenance',
    modifiedDate: new Date('2024-01-15'),
    children: [
      {
        id: 'doc-3-1',
        name: 'Maintenance Schedule 2024.xlsx',
        type: 'excel',
        path: '/Maintenance/Maintenance Schedule 2024.xlsx',
        size: 450000,
        modifiedDate: new Date('2024-01-01')
      },
      {
        id: 'doc-3-2',
        name: 'Work Orders',
        type: 'folder',
        path: '/Maintenance/Work Orders',
        modifiedDate: new Date('2024-01-15'),
        children: [
          {
            id: 'doc-3-2-1',
            name: 'WO_2024_001_INV3_Maintenance.pdf',
            type: 'pdf',
            path: '/Maintenance/Work Orders/WO_2024_001_INV3_Maintenance.pdf',
            size: 320000,
            modifiedDate: new Date('2024-01-15T12:00:00')
          }
        ]
      }
    ]
  }
];

// Helper function to get color by event source
export const getEventSourceColor = (source: Event['source']): string => {
  const colors = {
    'SCADA': '#3b82f6',    // blue
    'Manual': '#10b981',   // green
    'Alarm': '#f59e0b',    // amber
    'Maintenance': '#8b5cf6', // purple
    'Weather': '#06b6d4'   // cyan
  };
  return colors[source] || '#6b7280';
};

// Helper function to format file size
export const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

// Component metadata interfaces
export interface MetadataHistoryEntry {
  id: string;
  timestamp: Date;
  value: string | number;
  source: 'installation' | 'inspection' | 'maintenance' | 'update' | 'system';
  notes?: string;
  updatedBy?: string;
}

export interface ComponentMetadata {
  id: string;
  type: 'combiner' | 'inverter' | 'mv_station';
  label: string;
  powerRating: {
    current: number; // kW
    unit: 'kW';
    history: MetadataHistoryEntry[];
  };
  moduleCount: {
    current: number;
    history: MetadataHistoryEntry[];
  };
  manufacturer: {
    current: string;
    history: MetadataHistoryEntry[];
  };
  model: {
    current: string;
    history: MetadataHistoryEntry[];
  };
  serialNumber: {
    current: string;
    history: MetadataHistoryEntry[];
  };
  inspectionDate: {
    current: Date;
    history: MetadataHistoryEntry[];
  };
  status: {
    current: 'operational' | 'maintenance' | 'fault' | 'offline';
    history: MetadataHistoryEntry[];
  };
  installationDate: Date;
  location?: {
    lat: number;
    lng: number;
  };
}

// Generic combiner metadata template
export const genericCombinerMetadata: ComponentMetadata = {
  id: 'generic-combiner',
  type: 'combiner',
  label: 'Combiner Box',
  powerRating: {
    current: 15.2,
    unit: 'kW',
    history: [
      {
        id: 'pr-1',
        timestamp: new Date('2023-06-15T08:00:00'),
        value: 15.2,
        source: 'installation',
        notes: 'Initial installation rating',
        updatedBy: 'Installation Team'
      }
    ]
  },
  moduleCount: {
    current: 24,
    history: [
      {
        id: 'mc-1',
        timestamp: new Date('2023-06-15T08:00:00'),
        value: 24,
        source: 'installation',
        notes: 'Initial module configuration',
        updatedBy: 'Installation Team'
      }
    ]
  },
  manufacturer: {
    current: 'SolarEdge',
    history: [
      {
        id: 'mf-1',
        timestamp: new Date('2023-06-15T08:00:00'),
        value: 'SolarEdge',
        source: 'installation',
        updatedBy: 'Installation Team'
      }
    ]
  },
  model: {
    current: 'SE-CB-24-400V',
    history: [
      {
        id: 'md-1',
        timestamp: new Date('2023-06-15T08:00:00'),
        value: 'SE-CB-24-400V',
        source: 'installation',
        updatedBy: 'Installation Team'
      }
    ]
  },
  serialNumber: {
    current: 'SE240815001',
    history: [
      {
        id: 'sn-1',
        timestamp: new Date('2023-06-15T08:00:00'),
        value: 'SE240815001',
        source: 'installation',
        updatedBy: 'Installation Team'
      }
    ]
  },
  inspectionDate: {
    current: new Date('2024-01-15T14:30:00'),
    history: [
      {
        id: 'id-1',
        timestamp: new Date('2023-06-15T16:00:00'),
        value: '2023-06-15T16:00:00',
        source: 'installation',
        notes: 'Initial installation inspection',
        updatedBy: 'QA Team'
      },
      {
        id: 'id-2',
        timestamp: new Date('2023-12-15T10:00:00'),
        value: '2023-12-15T10:00:00',
        source: 'inspection',
        notes: '6-month routine inspection - all connections secure',
        updatedBy: 'Inspector John Doe'
      },
      {
        id: 'id-3',
        timestamp: new Date('2024-01-15T14:30:00'),
        value: '2024-01-15T14:30:00',
        source: 'inspection',
        notes: 'Annual inspection - minor corrosion on housing, cleaned',
        updatedBy: 'Inspector Jane Smith'
      }
    ]
  },
  status: {
    current: 'operational',
    history: [
      {
        id: 'st-1',
        timestamp: new Date('2023-06-15T08:00:00'),
        value: 'operational',
        source: 'installation',
        updatedBy: 'Installation Team'
      },
      {
        id: 'st-2',
        timestamp: new Date('2023-11-03T09:15:00'),
        value: 'maintenance',
        source: 'maintenance',
        notes: 'Scheduled maintenance - cleaning and connection check',
        updatedBy: 'Maintenance Team'
      },
      {
        id: 'st-3',
        timestamp: new Date('2023-11-03T11:45:00'),
        value: 'operational',
        source: 'maintenance',
        notes: 'Maintenance completed, all systems nominal',
        updatedBy: 'Maintenance Team'
      }
    ]
  },
  installationDate: new Date('2023-06-15T08:00:00'),
  location: {
    lat: 33.4484,
    lng: -112.0740
  }
};

// Helper function to get metadata for a component
export const getComponentMetadata = (componentId: string): ComponentMetadata | null => {
  // For now, return generic combiner metadata for any component ID
  // This assumes all selected components are combiner boxes
  if (componentId && componentId.trim() !== '') {
    return {
      ...genericCombinerMetadata,
      id: componentId,
      label: componentId // Use the actual component ID as the label
    };
  }
  return null;
};